import React from "react";
import {formatDateTime} from "../format.js";

function changedDeclarations(previous, revision) {
    const before = new Map((previous?.declarations || []).map((item) => [item.symbol, item.digest]));
    const changed = [];
    revision.declarations.forEach((item) => {
        if (before.get(item.symbol) !== item.digest) changed.push(item.symbol);
        before.delete(item.symbol);
    });
    return [...changed, ...before.keys()].sort();
}

export function RevisionHistory({revisions = [], usage = []}) {
    if (!revisions.length) return <div className="empty">No strategy revisions registered.</div>;
    const ordered = [...revisions].sort((a, b) => a.number - b.number);
    const latest = ordered.at(-1);
    return (
        <section className="detail-drawer-section revision-history">
            <h3>Strategy revisions</h3>
            <div className="subtle">
                Latest registered: revision {latest.number}. Registration does not activate a revision.
            </div>
            <div className="timeline">
                {[...ordered].reverse().map((revision) => {
                    const previous = ordered.find((item) => item.number === revision.number - 1);
                    const changed = changedDeclarations(previous, revision);
                    const observations = usage.filter((item) => item.execution.digest === revision.digest);
                    return (
                        <details className="timeline-item" key={revision.digest}>
                            <summary>
                                Revision {revision.number} · {formatDateTime(revision.createdAt)}
                                <div className="subtle mono">{revision.digest}</div>
                            </summary>
                            <div>
                                <div className="subtle">{previous ? "Changed rules" : "Initial rules"}</div>
                                <ul>{changed.map((symbol) => <li key={symbol}><code>{symbol}</code></li>)}</ul>
                                <div className="subtle">Committed sources</div>
                                <ul>
                                    {revision.sources.map((source) => {
                                        const sourcePath = source.path.split("/").map(encodeURIComponent).join("/");
                                        const href = `https://github.com/${source.repository}/blob/${source.commit}/${sourcePath}`;
                                        return (
                                            <li key={`${source.repository}:${source.commit}:${source.path}`}>
                                                <a href={href} target="_blank" rel="noreferrer">
                                                    {source.repository}/{source.path} · {source.commit.slice(0, 12)}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="subtle">Evidence and observed usage</div>
                                {!observations.length && <p>No execution has reported this revision.</p>}
                                {observations.map((item) => (
                                    <details key={`${item.kind}:${item.runtimeInstance || item.canonicalRunId}:${item.observedAt}`}>
                                        <summary>
                                            {item.kind === "qualification" ? "Qualification" : item.running ? "Running" : "Observed live"}
                                            {" · "}{item.account || item.canonicalRunId}
                                            {" · "}{formatDateTime(item.observedAt)}
                                        </summary>
                                        {item.evidenceUrl && <a href={item.evidenceUrl} target="_blank" rel="noreferrer">View evidence</a>}
                                        {item.runtimeInstance && <p>{item.runtimeInstance} · last heartbeat {formatDateTime(item.heartbeatAt)}</p>}
                                        {item.observedFrom && <p>Revision first observed {formatDateTime(item.observedFrom)}</p>}
                                        <div className="subtle">Build artifacts</div>
                                        <ul>{Object.entries(item.execution.buildArtifacts).map(([name, digest]) => (
                                            <li key={name}>{name}: <code>{digest}</code></li>
                                        ))}</ul>
                                        <div className="subtle">Effective configuration</div>
                                        <code>{item.execution.configurationDigest}</code>
                                        <pre>{item.configurationJson}</pre>
                                    </details>
                                ))}
                            </div>
                        </details>
                    );
                })}
            </div>
        </section>
    );
}
